import {
  BaseApplicant as Applicant,
  ESessionStorageKeys,
  Loader,
  Merchant,
  StepperFormFooter,
  useMerchantById,
} from '@applyonline/shared'
import { scrollToTop } from '@applyonline/shared/utils'
import { EFinancingProgram } from '@if/core/types'
import { AxiosError } from 'axios'
import { useCallback, useEffect, useLayoutEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import {
  CreditApplication,
  HomeInformationInput,
  PersonalInformationInput,
} from '../../types/Schemas/CreditApplicationSchema'
import AddressForm from './components/AddressForm'
import EmploymentForm from './components/EmploymentForm'
import PersonalInformationForm from './components/PersonalInformationForm'
import ReviewCreditApplication from './components/ReviewCreditApplication'
import StepProgressionBar from './components/StepProgessionBar'
import { buildDefaultCreditApplication, useNewApplicationStepList, useSessionStorage } from './submit-app-hooks'

interface SubmitNewAppFormProps {
  initialMerchant: Merchant
}
const SubmitNewAppForm = ({ initialMerchant }: SubmitNewAppFormProps) => {
  const { merchantId: urlMerchantId, currentStep } = useParams()
  const navigate = useNavigate()

  const getStoredOrDefaultApp = () => {
    const savedCreditApplicationInfo = sessionStorage.getItem(ESessionStorageKeys.CREDIT_APPLICATION_INFO)
    if (savedCreditApplicationInfo) {
      return JSON.parse(savedCreditApplicationInfo) as CreditApplication
    }
    return buildDefaultCreditApplication(initialMerchant, false)
  }

  const [creditApplicationInfo, setCreditApplicationInfo] = useSessionStorage<CreditApplication>(
    ESessionStorageKeys.CREDIT_APPLICATION_INFO,
    getStoredOrDefaultApp(),
  )

  useEffect(() => {
    const savedCreditApplicationInfo = sessionStorage.getItem(ESessionStorageKeys.CREDIT_APPLICATION_INFO)
    if (!savedCreditApplicationInfo) {
      navigate(`/${urlMerchantId}/new-app/personal-information`, { replace: true })
    }
  }, [urlMerchantId, navigate])

  const [disableNext, setDisableNext] = useState(false)

  const steps = useNewApplicationStepList(creditApplicationInfo.isSinglePayment)

  const cls = `form-section step-${currentStep}-active`

  useLayoutEffect(scrollToTop, [])

  const gotoStep = useCallback(
    (stepPath: string) => {
      scrollToTop()
      navigate(`/${urlMerchantId}/new-app/${stepPath}`)
    },
    [navigate, urlMerchantId],
  )

  const nextStepAfter = useCallback(() => {
    const nextStepIdx = currentStep ? steps.indexOf(currentStep.toLowerCase()) + 1 : 1
    if (nextStepIdx < steps.length) {
      gotoStep(steps[nextStepIdx])
    }
  }, [currentStep, gotoStep, steps])

  const handleCreditApplicationChanged = (data: PersonalInformationInput) => {
    let updatedCreditApplicationInfo
    if (data.applicant) {
      const { applicant, ...otherData } = data
      updatedCreditApplicationInfo = {
        ...creditApplicationInfo,
        ...otherData,
        applicant: { ...creditApplicationInfo.applicant, ...applicant },
      }
    }

    setCreditApplicationInfo(updatedCreditApplicationInfo as CreditApplication)
  }

  const handleApplicantChanged = (data: Partial<Applicant>) => {
    const update = { ...creditApplicationInfo, applicant: { ...creditApplicationInfo.applicant, ...data } }
    setCreditApplicationInfo(update)
  }

  const handleAddressUpdated = (data: HomeInformationInput) => {
    const applicantData: Partial<Applicant> = {
      currentAddress: data.currentAddress,
      spending: data.spending || undefined,
    }
    handleApplicantChanged(applicantData)
  }
  return (
    <main className={cls}>
      <StepProgressionBar steps={steps} currentStep={currentStep!} />

      {currentStep === 'personal-information' && (
        <PersonalInformationForm
          creditApplicationData={creditApplicationInfo}
          onCreditApplicationChanged={handleCreditApplicationChanged}
          nextStepAfter={nextStepAfter}
          currentStep={currentStep}
        />
      )}

      {currentStep === 'address' && (
        <AddressForm
          applicantData={creditApplicationInfo.applicant}
          nextStepAfter={nextStepAfter}
          currentStep={currentStep}
          onApplicantChanged={handleAddressUpdated}
          applicantName={creditApplicationInfo.applicant.firstName}
          applicantBirthDate={creditApplicationInfo.applicant.birthDate}
          isSinglePayment={creditApplicationInfo.isSinglePayment}
          setDisableNext={setDisableNext}
        />
      )}

      {currentStep === 'employment' && (
        <EmploymentForm
          applicantData={creditApplicationInfo.applicant}
          onApplicantChanged={handleApplicantChanged}
          nextStepAfter={nextStepAfter}
          currentStep={currentStep}
        />
      )}

      {currentStep === 'review' && (
        <ReviewCreditApplication gotoStep={gotoStep} creditApplication={creditApplicationInfo} />
      )}

      {currentStep !== 'review' && (
        <StepperFormFooter
          currentStepIndex={steps.indexOf(currentStep!)}
          disableBack={steps.indexOf(currentStep!) === 0}
          disableNext={disableNext}
          steps={steps}
          baseURL={`/${urlMerchantId}/new-app`}
        />
      )}
    </main>
  )
}

const SubmitNewAppPage = () => {
  const urlParams = useParams()
  const urlMerchantId = urlParams.merchantId!
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [merchant, isFetchingMerchant, error] = useMerchantById(urlMerchantId, EFinancingProgram.MechanicalWarranty)

  useEffect(() => {
    if (error) {
      if ((error as AxiosError)?.response?.status === 403) {
        navigate('/get-merchant-error/inactive')
      } else if ((error as AxiosError)?.response?.status === 404) {
        navigate('/get-merchant-error/not-found')
      } else {
        navigate('/general-error-page/error')
      }
    }
  }, [error, navigate])

  if (isFetchingMerchant) return <Loader title={t('common.withYouShortly')} description={t('common.pleaseWait')} />
  if (error) return null
  if (merchant) return <SubmitNewAppForm initialMerchant={merchant} />
  return null
}

export default SubmitNewAppPage
